/**
 * Which business the app is looking at, and what it costs to change it.
 *
 * The decisions live here rather than in the provider for one reason: a switch
 * is the most destructive-looking thing in an offline-first app — it changes
 * what every screen reads, in one frame, while a shop is standing at a counter
 * — and it has no renderer in this repo to be tested through. Everything below
 * is plain functions over plain values with the two side effects injected, so
 * `active-tenant.sqlite.test.ts` drives the REAL sequence against the REAL
 * repository on a real SQLite, and `use-active-tenant.tsx` is left holding
 * nothing but React.
 *
 * **Nothing is wiped by a switch.** The device database is tenant-scoped
 * (migration 6), so a business already synced here draws its records from the
 * mirror the instant the predicate changes. A switch is a change of predicate,
 * not a resync — see docs/plans/multi-business-mobile.md, decision A.
 */

/** The little of a membership any of this needs. `MembershipSummary` fits. */
export type TenantMembership = { readonly tenantId: string };

const belongsTo = (
  tenantId: string,
  memberships: readonly TenantMembership[],
): boolean => memberships.some((membership) => membership.tenantId === tenantId);

/**
 * Which business is active, in order: the stored pick when it is still one of
 * theirs, else the first membership, else none.
 *
 * The middle rule is the one that matters. A stored pick names a business this
 * person may have been removed from since — by an owner, on another device,
 * while this phone was in a pocket. Honouring it would strand the app on a
 * business whose every read is empty and whose every write is refused, with no
 * way back except reinstalling. Falling back to a business they DO belong to
 * costs them a switch they did not ask for, once, and heals itself.
 *
 * `/v1/me` is the authority on that list, and it is cached on the device, so
 * this answers the same thing offline as it does online.
 */
export const resolveActiveTenant = (
  stored: string | null,
  memberships: readonly TenantMembership[],
): string | null => {
  if (stored !== null && belongsTo(stored, memberships)) return stored;
  return memberships[0]?.tenantId ?? null;
};

/**
 * Resolve, and write the answer down when it had to fall back.
 *
 * The write is not bookkeeping. Every repository function takes its tenant as
 * a trailing optional argument and falls back to the stored pick when a caller
 * names none, so a stored pick that no longer resolves would have the provider
 * and the repository disagreeing about which business is open — the provider
 * reading business A while an unnamed call read the ghost and answered nothing.
 * One write keeps the two in step.
 */
export const adoptActiveTenant = (
  memberships: readonly TenantMembership[],
  deps: {
    readonly read: () => string | null;
    readonly write: (tenantId: string) => void;
  },
): string | null => {
  const stored = deps.read();
  const resolved = resolveActiveTenant(stored, memberships);
  if (resolved !== null && resolved !== stored) deps.write(resolved);
  return resolved;
};

/**
 * How long a switch will wait for the business being left to drain its outbox.
 *
 * Long enough for a healthy request over a shop's wifi, short enough that
 * nobody wonders whether the tap registered. It is a budget and not a
 * deadline: the sync it races is never cancelled, so a drain that needs longer
 * simply finishes after the screen has already changed.
 */
export const PARTING_SYNC_MS = 1200;

/**
 * Wait for a sync, but never for longer than the budget.
 *
 * The failure this exists to prevent: a switch that hangs on a dead network.
 * `sync` is serialized per tenant and swallows its own failures, so the
 * promise here resolves on its own eventually; "eventually" is not good enough
 * for something a finger is waiting on.
 */
const drain = async (
  sync: (tenantId: string) => Promise<void>,
  tenantId: string,
  budgetMs: number,
): Promise<void> => {
  const running = sync(tenantId).catch(() => undefined);
  let timer: ReturnType<typeof setTimeout> | null = null;
  const budget = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, budgetMs);
  });
  try {
    await Promise.race([running, budget]);
  } finally {
    if (timer !== null) clearTimeout(timer);
  }
};

export type SwitchDeps = {
  /** The sync engine. Called for the business being left, then the new one. */
  readonly sync: (tenantId: string) => Promise<void>;
  /** Persist the pick, so a crash cannot undo a switch. */
  readonly setActive: (tenantId: string) => void;
  /** Flip the state every screen reads. */
  readonly onSwitched: (tenantId: string) => void;
  readonly partingMs?: number;
};

/**
 * Change business. Answers false when the switch was refused.
 *
 * The order is the whole of it:
 *
 *   1. refuse a business this person does not belong to. The sheet only ever
 *      offers memberships, so this is a guard against a stale deep link or a
 *      programming mistake, and it fails by doing nothing rather than by
 *      leaving the app pointed at a business the server will refuse.
 *   2. one parting sync of the business being LEFT, under its own tenant id,
 *      bounded by `PARTING_SYNC_MS`. What it drains is gone before the screen
 *      changes; what it cannot drain is not lost and not re-owned — every op
 *      carries its own `tenant_id`, `push` reads only its own business's ops,
 *      and an undrained op waits, labelled, and is pushed the next time that
 *      business is active. Nothing is dropped by a switch.
 *   3. write the pick down, before anything renders, so a crash mid-switch
 *      leaves the device on the business the person chose.
 *   4. flip the state, which is what makes every screen re-read.
 *   5. kick a sync for the new business, not awaited: the mirror already holds
 *      whatever synced here before, so this is a catch-up and never a gate.
 *
 * Two and five are both `sync`, and they must be two calls and not one: the
 * engine serializes per tenant, and these are different tenants.
 */
export const switchActiveTenant = async (
  input: {
    readonly next: string;
    /** The business being left, or null when nothing was open. */
    readonly previous: string | null;
    readonly memberships: readonly TenantMembership[];
  },
  deps: SwitchDeps,
): Promise<boolean> => {
  if (!belongsTo(input.next, input.memberships)) return false;
  if (input.previous !== null && input.previous !== input.next) {
    await drain(deps.sync, input.previous, deps.partingMs ?? PARTING_SYNC_MS);
  }
  deps.setActive(input.next);
  deps.onSwitched(input.next);
  void deps.sync(input.next).catch(() => undefined);
  return true;
};
